// Taken from https://raw.githubusercontent.com/CosticaPuntaru/redux-form-validation/master/src/validation.js
// We initially used that library but it is unmaintained and incompatible with redux-form v6.
// TODO: we should probably fork it.

import { FormikValues } from "formik"

import { isString } from "../types"

type ValidationFunction = (
  field: string,
  value: string,
  prop: boolean,
) => string | false

const validationStore: Record<string, ValidationFunction> = {}

// eslint-disable-next-line no-return-assign
const addValidation = (key: string, fn: ValidationFunction) =>
  (validationStore[key] = fn)

export const generateFormikValidation = (
  config: Record<string, string[]>,
): ((values: FormikValues) => Record<string, string>) => {
  const pairs = Object.entries(config)

  return (values: FormikValues): Record<string, string> => {
    const errors: Record<string, string> = {}

    pairs.forEach(([fieldName, validators]) => {
      const isValidatorType =
        (type: string) => (validator: string | { name: string }) =>
          isString(validator) ? validator === type : validator.name === type
      const isRequiredType = isValidatorType("required")
      const isOneRequiredType = isValidatorType("oneRequired")

      const required =
        validators.find(isRequiredType) || validators.find(isOneRequiredType)

      if (!required && !values[fieldName]) {
        return
      }

      validators.forEach(validator => {
        const error = validationStore[validator](
          fieldName,
          values[fieldName],
          true,
        )
        if (error) errors[fieldName] = error
      })
    })
    return errors
  }
}

export const generateFormikValidationWithCustomErrors = (
  config: Record<string, string[]>,
  customErrorMessages: Record<string, string>,
  values: FormikValues,
): Record<string, string> => {
  const errors = generateFormikValidation(config)(values)
  Object.entries(customErrorMessages).forEach(([key, value]) => {
    if (errors[key]) {
      errors[key] = value
    }
  })
  return errors
}
// BEGIN VALIDATORS LOGIC

addValidation("required", (field: string, value: string, prop: boolean) =>
  prop ? !value && "This field is required" : false,
)

export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/
export function validateEmail(value: string) {
  // same regexp as we use on the back-end, which is URI:MailTo:EMAIL_REGEXP
  // source: https://stackoverflow.com/questions/22993545/ruby-email-validation-with-regex/22994329
  return value ? !EMAIL_REGEX.test(value) && "Invalid email address" : false
}

addValidation(
  "email",
  (_field: string, value: string, prop: boolean) =>
    prop && validateEmail(value),
)

addValidation(
  "isPositiveInteger",
  (_field: string, value: string, prop: boolean) => {
    const isPositiveInteger = (number: string) =>
      Number(number) > 0 && Number.isInteger(Number(number))
    return prop && value
      ? !isPositiveInteger(value) && "Please enter a positive number"
      : false
  },
)

const UNSUPPORTED_SMS_COUNTRY_CODES = [
  "65", // Singapore
]

addValidation("phone", (_field: string, value: string, prop: boolean) => {
  if (prop && value) {
    if (!/^(\+?\d{1,3})?\d{3}-?\d{3}-?\d{4}$/.test(value)) {
      return "Invalid phone number format"
    }
    UNSUPPORTED_SMS_COUNTRY_CODES.forEach(code => {
      if (new RegExp(`^(\\+|00)"${code}"`).test(value)) {
        return "Sending SMS to this country code is not supported"
      }
    })

    return false
  }

  return false
})
